import { FC, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useFineAttachmentsApi } from "../../http/fineAttachments/api";
import { FineAttachment, RenameFineAttachment } from "../../http/fineAttachments/types";
import { isConflict } from "../../http/errors";
import { PaginationQuery } from "../../types";
import { formatTimeStamp, isEmbed, isImage, isMobile, isVideo } from "../../types/utils";
import SvgEdit from "../icons/Edit";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { Action, getDeleteAction, getDownloadAction, getPreviewAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import DeleteFineAttachmentDialog from "./DeleteFineAttachmentDialog";
import UploadFineAttachment from "./UploadFineAttachment";

type FineAttachmentsProps = {
  setIsDragging: (isDragging: boolean) => void;
};

const FineAttachments: FC<FineAttachmentsProps> = ({ setIsDragging }) => {
  const i18n = useI18n();
  const fineAttachmentsApi = useFineAttachmentsApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const { id: fineId } = useParams<{ id: string }>();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [itemToDelete, setItemToDelete] = useState<FineAttachment>();
  const [documentIdToRename, setDocumentIdToRename] = useState<string>("");
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const api = useApi();
  const canUploadDocuments = userSettings.permissions.fines.insert;

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = fineAttachmentsApi.useFineAttachments(query, aborter.current.signal, fineId!);
  const [data] = useDataInitializer(result.data, result.error);

  const uploadFineAttachment = async (file: File): Promise<TableUploadFileResult> => {
    try {
      await fineAttachmentsApi.insertFineAttachment(file, false, fineId);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            fineAttachmentsApi
              .insertFineAttachment(file, true, fineId)
              .then(() => {
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const onSubmit = async (id: string, name: string, callback: () => void): Promise<void> => {
    const body: RenameFineAttachment = { name };

    try {
      await fineAttachmentsApi.renameFineAttachment(id, body, fineId);
      callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const downloadAttachment = (item: FineAttachment) =>
    api
      .downloadFile(`/api/admin/fine_attachments/${item.id}` + `?t=${new Date().getTime()}`, item.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const onPreviewClick = async (item: FineAttachment) => {
    if (!isMobile && (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))) {
      const docUrl = await api.createObjectURL(
        `/api/admin/fine_attachments/${item.id}` + `?t=${new Date().getTime()}`,
        true,
      );
      setItemToPreview({ name: item.name, url: docUrl });
      return;
    }
    downloadAttachment(item);
  };

  const getRowActions = (item: FineAttachment): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.fines.read) {
      actions.push(getDownloadAction(i18n, () => downloadAttachment(item)));
    }

    if (
      userSettings.permissions.fines.read &&
      !isMobile &&
      (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))
    ) {
      actions.push(getPreviewAction(i18n, () => onPreviewClick(item)));
    }

    if (userSettings.permissions.fines.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setDocumentIdToRename(item.id),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.fines.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Table<FineAttachment>
        filters={filters}
        fileDrop={{
          enabled: canUploadDocuments,
          onUploadFile: uploadFineAttachment,
          isDraggingOn: setIsDragging,
        }}
        buttons={{
          main: canUploadDocuments && !!fineId && <UploadFineAttachment fineId={fineId} />,
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.uploadedBy}</th>
            <th align="left">{i18n.t.common.uploadedAt}</th>
            <th align="left">{i18n.t.common.modifiedBy}</th>
            <th align="left">{i18n.t.common.modifiedAt}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <Table.EditableCell
              defaultName={item.name}
              itemId={item.id}
              onSubmit={onSubmit}
              onCancel={() => setDocumentIdToRename("")}
              isEditing={item.id === documentIdToRename}
              disabled={!userSettings.permissions.fines.update}
            >
              <DownloadLink url={`/api/admin/fine_attachments/${item.id}`} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </Table.EditableCell>
            <td>{item.uploader.fullName}</td>
            <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
            <td>{item.editor?.fullName ?? ""}</td>
            <td>{item.editor && item.modifiedAt ? formatTimeStamp(item.modifiedAt, i18n.language) : ""}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <DeleteFineAttachmentDialog fineAttachment={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default FineAttachments;
